import { createStyles, makeStyles } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { CatalogueProduct } from '../../../api/products/catalogue';
import { useAddToCart } from '../../../api/shoppingCart';
import AddToCartButtonToQty from '../../../components/AddToCartButtonToQty';
import { isAuthenticatedState } from '../../../api/appState';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import CataloguePageListContentItemPackageButtons from './CataloguePageListContentItemPackageButtons';

export interface CataloguePageListContentItemCardAddToCartProps
  extends StandardDivProps<CataloguePageListContentItemCardAddToCartClassKey> {
  product: CatalogueProduct;
}

export type CataloguePageListContentItemCardAddToCartClassKey =
  | 'root'
  | 'topRow'
  | 'bottomRow'
  | 'qfWrapper'
  | 'cartInfo';

const themeSettings = { name: 'CataloguePageListContentItemCardAddToCart' };

const useStyles = makeStyles(
  createStyles<CataloguePageListContentItemCardAddToCartClassKey, {}>({
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },

    topRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },

    bottomRow: {
      display: 'flex',
      justifyContent: 'flex-start',
      marginTop: 10,
      width: '100%',
    },

    qfWrapper: {
      display: 'flex',
      alignItems: 'center',
    },

    cartInfo: {
      backgroundColor: '#002969',
      color: 'white',
      padding: '4px 8px',
      borderRadius: 4,
      fontSize: 12,
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
  }),
  themeSettings,
);

const CataloguePageListContentItemCardAddToCart = React.forwardRef<
  HTMLDivElement,
  CataloguePageListContentItemCardAddToCartProps
>(({ className, classes: pClasses, product }, ref) => {
  const classes = useStyles({ classes: pClasses });
  const isAuthenticated = useRecoilValue(isAuthenticatedState);
  const t = useTranslateWithFallback();

  const {
    addToCart,
    quantity = 0,
    ...addToCartProps
  } = useAddToCart({
    forcePreventDecrement: true,
    product,
  });

  return (
    <div ref={ref} className={clsx(classes.root, className)}>
      {/* TOP ROW */}
      <div className={classes.topRow}>
        {/* LEFT: Quantity control */}
        <div className={classes.qfWrapper}>
          <AddToCartButtonToQty {...addToCartProps} product={product} quantity={quantity} onAddToCart={addToCart} />
        </div>

        {/* RIGHT: Cart info */}
        {quantity > 0 && (
          <div className={classes.cartInfo}>
            <strong>
              {quantity} {t(quantity === 1 ? 'app/pcsInCartUnitSingular' : 'app/pcsInCartUnitPlural')}
            </strong>{' '}
            {t('app/pcsInCart')}
          </div>
        )}
      </div>

      {/* BOTTOM ROW: Package buttons */}
      {isAuthenticated && (
        <>
          <div className={classes.bottomRow}>
            <CataloguePageListContentItemPackageButtons product={product} />
          </div>
        </>
      )}
    </div>
  );
});

CataloguePageListContentItemCardAddToCart.displayName = 'CataloguePageListContentItemCardAddToCart';

export default withThemeProps(themeSettings)(CataloguePageListContentItemCardAddToCart);
